import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Paper,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Chip,
    Divider,
    Tabs,
    Tab,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ArrowLeft,
    Server,
    Globe,
    Computer,
    Activity,
    Wifi,
    Database,
    RefreshCw
} from 'lucide-react';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectConnectedEnginesSupport, ConnectedSocket } from '@common/reducers/ConnectedEnginesSlice';
import { 

    useGetEngineStatusMutation,
    usePingEngineMutation
} from '@common-reducers/backend-api/backend-api';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`engine-tabpanel-${index}`}
            aria-labelledby={`engine-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

export default function EngineExplorer() {
    const { engineId } = useParams<{ engineId: string }>();
    const navigate = useNavigate();
    const engines: ConnectedSocket[] = useAppSelector(selectConnectedEnginesSupport);
    
    // Helper function to safely render any value that might be an object
    const safeRender = (value: any, fallback: string = 'Unknown') => {
        if (value === null || value === undefined) return fallback;
        if (typeof value === 'string') return value;
        if (typeof value === 'number') return value.toString();
        if (typeof value === 'boolean') return value.toString();
        if (typeof value === 'object') {
            if (value._serialized) return value._serialized;
            return JSON.stringify(value);
        }
        return String(value);
    };
    
    // State management
    const [activeTab, setActiveTab] = useState(0);
    const [engineStatus, setEngineStatus] = useState<any>(null);
    
    // Get current URL search params to preserve supportedUserId
    const searchParams = new URLSearchParams(window.location.search);
    const supportedUserId = searchParams.get('supportedUserId');
    
    // Find the specific engine by ID
    const engine = engines.find(e => e.id === engineId);

    // RTK Mutations
    const [getEngineStatus, { isLoading: statusLoading, error: statusError }] = useGetEngineStatusMutation();
    const [pingEngine, { isLoading: pingLoading, error: pingError }] = usePingEngineMutation();

    useEffect(() => {
        if (!engine) {
            // Redirect back to user overview if engine not found, preserving supportedUserId
            const userOverviewPath = '/user-overview';
            const redirectUrl = supportedUserId 
                ? `${userOverviewPath}?supportedUserId=${supportedUserId}`
                : userOverviewPath;
            navigate(redirectUrl);
        }
    }, [engine, navigate, supportedUserId]);

    // Load initial data when component mounts
    useEffect(() => {
        if (engine && engineId) {
            loadEngineStatus();
        }
    }, [engine, engineId]);

    // Action functions
    const loadEngineStatus = async () => {
        try {
            const result = await getEngineStatus({ engineId: engineId! }).unwrap();
            setEngineStatus(result);
        } catch (error) {
            console.error('Failed to load engine status:', error);
        }
    };

    const handlePing = async () => {
        try {
            const result = await pingEngine({ engineId: engineId! }).unwrap();
            console.log('Ping result:', result);
        } catch (error) {
            console.error('Failed to ping engine:', error);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    if (!engine) {
        return null;
    }

    const getEngineIcon = (browserType: string) => {
        switch (browserType) {
            case 'Gateway':
                return Server;
            case 'WhatsApp Web':
                return Globe;
            case 'Website':
                return Computer;
            default:
                return Computer;
        }
    };

    const getEngineColor = (browserType: string) => {
        switch (browserType) {
            case 'Gateway':
                return 'text-blue-600';
            case 'WhatsApp Web':
                return 'text-green-600';
            case 'Website':
                return 'text-orange-600';
            default:
                return 'text-gray-600';
        }
    };

    const formatConnectionTime = (connectionTime: string) => {
        try {
            const date = new Date(connectionTime);
            return date.toLocaleString('en-US', {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } catch (error) {
            return 'Unknown';
        }
    };

    const Icon = getEngineIcon(engine.browserType);
    const colorClass = getEngineColor(engine.browserType);

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <Paper className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <Button
                            startIcon={<ArrowLeft />}
                            onClick={() => {
                                const userOverviewPath = '/user-overview';
                                const backUrl = supportedUserId 
                                    ? `${userOverviewPath}?supportedUserId=${supportedUserId}`
                                    : userOverviewPath;
                                navigate(backUrl);
                            }}
                            variant="outlined"
                            color="inherit"
                        >
                            Back to User Overview
                        </Button>
                        <div className="flex gap-2">
                            <Chip
                                label="Live Engine"
                                color="success"
                                variant="outlined"
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={pingLoading ? <CircularProgress size={16} /> : <Wifi />}
                                onClick={handlePing}
                                disabled={pingLoading}
                            >
                                Ping
                            </Button>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                        <div className={`p-4 rounded-lg bg-gray-100`}>
                            <Icon className={`h-8 w-8 ${colorClass}`} />
                        </div>
                        <div>
                            <Typography variant="h4" className="font-bold text-gray-900">
                                {engine.browserType} Engine
                            </Typography>
                            <Typography variant="subtitle1" className="text-gray-600">
                                {engine.domain}
                            </Typography>
                        </div>
                    </div>
                </Paper>

                {/* Main Content with Tabs */}
                <Paper className="p-0">
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={activeTab} onChange={handleTabChange} aria-label="engine explorer tabs">
                            <Tab icon={<Activity />} label="Status" />
                            <Tab icon={<Database />} label="Connection Info" />
                        </Tabs>
                    </Box>

                    {/* Status Tab */}
                    <TabPanel value={activeTab} index={0}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <div className="flex items-center justify-between mb-4">
                                    <Typography variant="h6">Engine Status & Capabilities</Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={statusLoading ? <CircularProgress size={16} /> : <RefreshCw />}
                                        onClick={loadEngineStatus}
                                        disabled={statusLoading}
                                    >
                                        Refresh Status
                                    </Button>
                                </div>
                                {statusError && (
                                    <Alert severity="error" className="mb-4">
                                        Failed to load engine status: {(statusError as any)?.message || 'Unknown error'}
                                    </Alert>
                                )}
                                {statusLoading ? (
                                    <div className="flex justify-center p-8">
                                        <CircularProgress />
                                    </div>
                                ) : engineStatus ? (
                                    <Card>
                                        <CardContent>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" className="text-gray-600">WhatsApp State:</Typography>
                                                    <Chip
                                                        label={safeRender(engineStatus.whatsappState, 'Unknown')}
                                                        color={engineStatus.whatsappState === 'CONNECTED' ? 'success' : 'warning'}
                                                        size="small"
                                                        className="mt-1"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" className="text-gray-600">Engine Type:</Typography>
                                                    <Typography>{safeRender(engineStatus.engineType, 'Unknown')}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" className="text-gray-600">Last Update:</Typography>
                                                    <Typography>{engineStatus.lastUpdate ? new Date(engineStatus.lastUpdate).toLocaleString() : 'Not available'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" className="text-gray-600">Capabilities:</Typography>
                                                    <div className="flex gap-1 flex-wrap mt-1">
                                                        {engineStatus.capabilities ? 
                                                            Object.entries(engineStatus.capabilities)
                                                                .filter(([_, enabled]) => enabled)
                                                                .map(([capability, _], index) => {
                                                                    // Convert camelCase to readable labels
                                                                    const label = capability.replace(/^can/, '').replace(/([A-Z])/g, ' $1').trim();
                                                                    return (
                                                                        <Chip key={index} label={label} size="small" variant="outlined" color="success" />
                                                                    );
                                                                })
                                                            : <Typography variant="body2">No capabilities reported</Typography>
                                                        }
                                                    </div>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <Alert severity="info">No status data available. Click "Refresh Status" to load.</Alert>
                                )}
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* Connection Info Tab */}
                    <TabPanel value={activeTab} index={1}>
                        <Grid container spacing={4}>
                            {/* Connection Information */}
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardContent>
                                        <div className="flex items-center gap-2 mb-4">
                                            <Activity className="h-5 w-5 text-blue-500" />
                                            <Typography variant="h6" className="font-semibold">
                                                Connection Details
                                            </Typography>
                                        </div>
                                        <Divider className="mb-4" />
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center">
                                                <Typography variant="body2" className="text-gray-600">
                                                    Engine Type:
                                                </Typography>
                                                <Chip
                                                    label={engine.browserType}
                                                    size="small"
                                                    className="bg-blue-100 text-blue-800"
                                                />
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <Typography variant="body2" className="text-gray-600">
                                                    Domain:
                                                </Typography>
                                                <Typography variant="body2" className="font-medium">
                                                    {safeRender(engine.domain, 'Unknown')}
                                                </Typography>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <Typography variant="body2" className="text-gray-600">
                                                    Connected Since:
                                                </Typography>
                                                <Typography variant="body2" className="font-medium">
                                                    {formatConnectionTime(engine.connectionTime)}
                                                </Typography>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <Typography variant="body2" className="text-gray-600">
                                                    Device:
                                                </Typography>
                                                <Typography variant="body2" className="font-medium">
                                                    {safeRender(engine.device, 'Unknown')}
                                                </Typography>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <Typography variant="body2" className="text-gray-600">
                                                    Connected From:
                                                </Typography>
                                                <Typography variant="body2" className="font-medium">
                                                    {safeRender(engine.connectedFrom, 'Unknown')}
                                                </Typography>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Engine User Information */}
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardContent>
                                        <div className="flex items-center gap-2 mb-4">
                                            <Activity className="h-5 w-5 text-blue-500" />
                                            <Typography variant="h6" className="font-semibold">
                                                Session Information
                                            </Typography>
                                        </div>
                                        <Divider className="mb-4" />
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center">
                                                <Typography variant="body2" className="text-gray-600">
                                                    Engine ID:
                                                </Typography>
                                                <Typography variant="body2" className="font-mono text-xs">
                                                    {safeRender(engine.id)}
                                                </Typography>
                                            </div>
                                            {engine.user && (
                                                <>
                                                    <div className="flex justify-between items-center">
                                                        <Typography variant="body2" className="text-gray-600">
                                                            User Email:
                                                        </Typography>
                                                        <Typography variant="body2" className="font-medium">
                                                            {safeRender(engine.user.email)}
                                                        </Typography>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <Typography variant="body2" className="text-gray-600">
                                                            User Name:
                                                        </Typography>
                                                        <Typography variant="body2" className="font-medium">
                                                            {safeRender(engine.user.firstName)} {safeRender(engine.user.lastName)}
                                                        </Typography>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <Typography variant="body2" className="text-gray-600">
                                                            User ID:
                                                        </Typography>
                                                        <Typography variant="body2" className="font-mono text-xs">
                                                            {safeRender(engine.user._id)}
                                                        </Typography>
                                                    </div>
                                                </>
                                            )}
                                            <div className="flex justify-between items-center">
                                                <Typography variant="body2" className="text-gray-600">
                                                    Status:
                                                </Typography>
                                                <Chip
                                                    label="Connected"
                                                    color="success"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </TabPanel>
                </Paper>
            </div>
        </div>
    );
}